import { HTTP_METHODS } from 'constants/general';
import {
  ADMIN_WATCH_TOWER_COMPANIES,
  ADMIN_WATCH_TOWER_COMPANIES_CHATS,
  ADMIN_WATCH_TOWER_COMPANIES_CREATE,
  ADMIN_WATCH_TOWER_COMPANIES_IMPORT,
  ADMIN_WATCH_TOWER_COMPANY,
  ADMIN_WATCH_TOWER_COMPANY_CHATS,
  ADMIN_WATCH_TOWER_COMPANY_DOSSIER,
  ADMIN_WATCH_TOWER_COMPANY_INVESTIGATION_APPROVE,
  ADMIN_WATCH_TOWER_COMPANY_INVESTIGATION_CANCEL,
  ADMIN_WATCH_TOWER_COMPANY_INVESTIGATION_CONTINUE,
  ADMIN_WATCH_TOWER_COMPANY_INVESTIGATION_RETRY,
  ADMIN_WATCH_TOWER_COMPANY_RUNS,
  ADMIN_WATCH_TOWER_DIGEST_SEND,
  ADMIN_WATCH_TOWER_INCIDENT,
  ADMIN_WATCH_TOWER_INCIDENT_CHATS,
  ADMIN_WATCH_TOWER_INCIDENT_KNOWN_LIST,
  ADMIN_WATCH_TOWER_INCIDENT_MATCHER,
  ADMIN_WATCH_TOWER_INCIDENTS,
  ADMIN_WATCH_TOWER_RUN_LLM_STEPS,
  ADMIN_WATCH_TOWER_SCHEDULE,
  ADMIN_WATCH_TOWER_SCHEDULES,
  ADMIN_WATCH_TOWER_TOPIC,
  ADMIN_WATCH_TOWER_TOPICS,
} from 'constants/requestUrls';
import mainApi from 'store/apis/mainApi';
import unwrapWatchTowerBody from 'store/slices/watchTower/utils/unwrapWatchTowerBody';
import {
  WATCH_TOWER_CHATS_TAG,
  WATCH_TOWER_COMPANIES_TAG,
  WATCH_TOWER_COMPANY_TAG,
  WATCH_TOWER_INCIDENT_TAG,
  WATCH_TOWER_INCIDENTS_TAG,
  WATCH_TOWER_RUNS_TAG,
  WATCH_TOWER_SCHEDULES_TAG,
  WATCH_TOWER_TOPICS_TAG,
} from 'store/tags/watchTower';

import type {
  IWatchChatBind,
  IWatchCreateCompaniesRequest,
  IWatchCreateScheduleRequest,
  IWatchCreateTopicRequest,
  IWatchImportCommitResult,
  IWatchIncidentDetail,
  IWatchIncidentListItem,
  IWatchInvestigationContinueResult,
  IWatchIssueChatRecord,
  IWatchKnownListRequest,
  IWatchPatchTopicRequest,
} from '../interfaces/IWatchTower';

const companyIdUrl = (template: string, id: number) => template.replace('{id}', `${id}`);
const publicIdUrl = (template: string, publicId: string) => template.replace('{publicId}', publicId);

const watchTowerInvalidate = [
  WATCH_TOWER_COMPANIES_TAG,
  WATCH_TOWER_COMPANY_TAG,
  WATCH_TOWER_RUNS_TAG,
];

/**
 * Super Admin Watch Tower APIs. Nest answers with a bare entity or array.
 *
 * @see AdminWatchTowerController `admin/watch-tower`
 */
export const watchTowerApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    getWatchCompanies: builder.query<unknown[], { status?: string } | void>({
      query: (params) => ({
        url: ADMIN_WATCH_TOWER_COMPANIES,
        params: params?.status ? { status: params.status } : undefined,
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<unknown[]>(response),
      providesTags: [WATCH_TOWER_COMPANIES_TAG],
    }),

    getWatchCompany: builder.query<unknown, number>({
      query: (id) => ({
        url: companyIdUrl(ADMIN_WATCH_TOWER_COMPANY, id),
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<unknown>(response),
      providesTags: (_result, _error, id) => [{ type: WATCH_TOWER_COMPANY_TAG, id }],
    }),

    getWatchCompanyDossier: builder.query<unknown, number>({
      query: (id) => ({
        url: companyIdUrl(ADMIN_WATCH_TOWER_COMPANY_DOSSIER, id),
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<unknown>(response),
      providesTags: (_result, _error, id) => [{ type: WATCH_TOWER_COMPANY_TAG, id }],
    }),

    getWatchCompanyRuns: builder.query<unknown[], number>({
      query: (id) => ({
        url: companyIdUrl(ADMIN_WATCH_TOWER_COMPANY_RUNS, id),
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<unknown[]>(response),
      providesTags: (_result, _error, id) => [{ type: WATCH_TOWER_RUNS_TAG, id }],
    }),

    getWatchRunLlmSteps: builder.query<unknown[], number>({
      query: (runId) => ({
        url: ADMIN_WATCH_TOWER_RUN_LLM_STEPS.replace('{runId}', `${runId}`),
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<unknown[]>(response),
    }),

    importWatchCompanies: builder.mutation<IWatchImportCommitResult, unknown>({
      query: (body) => ({
        url: ADMIN_WATCH_TOWER_COMPANIES_IMPORT,
        method: HTTP_METHODS.POST,
        body,
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<IWatchImportCommitResult>(response),
      invalidatesTags: watchTowerInvalidate,
    }),

    createWatchCompanies: builder.mutation<IWatchImportCommitResult, IWatchCreateCompaniesRequest>({
      query: (body) => ({
        url: ADMIN_WATCH_TOWER_COMPANIES_CREATE,
        method: HTTP_METHODS.POST,
        body,
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<IWatchImportCommitResult>(response),
      invalidatesTags: watchTowerInvalidate,
    }),

    approveWatchInvestigation: builder.mutation<unknown, number>({
      query: (id) => ({
        url: companyIdUrl(ADMIN_WATCH_TOWER_COMPANY_INVESTIGATION_APPROVE, id),
        method: HTTP_METHODS.POST,
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<unknown>(response),
      invalidatesTags: (_result, _error, id) => [
        WATCH_TOWER_COMPANIES_TAG,
        { type: WATCH_TOWER_COMPANY_TAG, id },
        { type: WATCH_TOWER_RUNS_TAG, id },
      ],
    }),

    cancelWatchInvestigation: builder.mutation<unknown, number>({
      query: (id) => ({
        url: companyIdUrl(ADMIN_WATCH_TOWER_COMPANY_INVESTIGATION_CANCEL, id),
        method: HTTP_METHODS.POST,
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<unknown>(response),
      invalidatesTags: (_result, _error, id) => [
        WATCH_TOWER_COMPANIES_TAG,
        { type: WATCH_TOWER_COMPANY_TAG, id },
      ],
    }),

    retryWatchInvestigation: builder.mutation<unknown, number>({
      query: (id) => ({
        url: companyIdUrl(ADMIN_WATCH_TOWER_COMPANY_INVESTIGATION_RETRY, id),
        method: HTTP_METHODS.POST,
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<unknown>(response),
      invalidatesTags: (_result, _error, id) => [
        WATCH_TOWER_COMPANIES_TAG,
        { type: WATCH_TOWER_COMPANY_TAG, id },
        { type: WATCH_TOWER_RUNS_TAG, id },
      ],
    }),

    continueWatchInvestigation: builder.mutation<
    IWatchInvestigationContinueResult,
    { companyId: number, runId: number }
    >({
      query: ({ companyId, runId }) => ({
        url: companyIdUrl(ADMIN_WATCH_TOWER_COMPANY_INVESTIGATION_CONTINUE, companyId),
        method: HTTP_METHODS.POST,
        body: { runId },
      }),
      transformResponse: (response: unknown) => (
        unwrapWatchTowerBody<IWatchInvestigationContinueResult>(response)
      ),
      invalidatesTags: (_result, _error, { companyId }) => [
        WATCH_TOWER_COMPANIES_TAG,
        { type: WATCH_TOWER_COMPANY_TAG, id: companyId },
        { type: WATCH_TOWER_RUNS_TAG, id: companyId },
      ],
    }),

    openWatchCreateChat: builder.mutation<IWatchChatBind, void>({
      query: () => ({
        url: ADMIN_WATCH_TOWER_COMPANIES_CHATS,
        method: HTTP_METHODS.POST,
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<IWatchChatBind>(response),
      invalidatesTags: [WATCH_TOWER_CHATS_TAG],
    }),

    openWatchDossierChat: builder.mutation<IWatchChatBind, number>({
      query: (id) => ({
        url: companyIdUrl(ADMIN_WATCH_TOWER_COMPANY_CHATS, id),
        method: HTTP_METHODS.POST,
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<IWatchChatBind>(response),
      invalidatesTags: [WATCH_TOWER_CHATS_TAG],
    }),

    getWatchTopics: builder.query<unknown[], void>({
      query: () => ({
        url: ADMIN_WATCH_TOWER_TOPICS,
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<unknown[]>(response),
      providesTags: [WATCH_TOWER_TOPICS_TAG],
    }),

    getWatchTopic: builder.query<unknown, number>({
      query: (id) => ({
        url: companyIdUrl(ADMIN_WATCH_TOWER_TOPIC, id),
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<unknown>(response),
      providesTags: [WATCH_TOWER_TOPICS_TAG],
    }),

    createWatchTopic: builder.mutation<unknown, IWatchCreateTopicRequest>({
      query: (body) => ({
        url: ADMIN_WATCH_TOWER_TOPICS,
        method: HTTP_METHODS.POST,
        body,
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<unknown>(response),
      invalidatesTags: [WATCH_TOWER_TOPICS_TAG],
    }),

    patchWatchTopic: builder.mutation<unknown, IWatchPatchTopicRequest>({
      query: ({ id, ...body }) => ({
        url: companyIdUrl(ADMIN_WATCH_TOWER_TOPIC, id),
        method: HTTP_METHODS.PATCH,
        body,
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<unknown>(response),
      invalidatesTags: [WATCH_TOWER_TOPICS_TAG],
    }),

    getWatchSchedules: builder.query<unknown[], void>({
      query: () => ({
        url: ADMIN_WATCH_TOWER_SCHEDULES,
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<unknown[]>(response),
      providesTags: [WATCH_TOWER_SCHEDULES_TAG],
    }),

    getWatchSchedule: builder.query<unknown, number>({
      query: (id) => ({
        url: companyIdUrl(ADMIN_WATCH_TOWER_SCHEDULE, id),
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<unknown>(response),
      providesTags: [WATCH_TOWER_SCHEDULES_TAG],
    }),

    createWatchSchedule: builder.mutation<unknown, IWatchCreateScheduleRequest>({
      query: (body) => ({
        url: ADMIN_WATCH_TOWER_SCHEDULES,
        method: HTTP_METHODS.POST,
        body,
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<unknown>(response),
      invalidatesTags: [WATCH_TOWER_SCHEDULES_TAG],
    }),

    getWatchIncidents: builder.query<IWatchIncidentListItem[], void>({
      query: () => ({
        url: ADMIN_WATCH_TOWER_INCIDENTS,
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<IWatchIncidentListItem[]>(response),
      providesTags: [WATCH_TOWER_INCIDENTS_TAG],
    }),

    getWatchIncident: builder.query<IWatchIncidentDetail, string>({
      query: (publicId) => ({
        url: publicIdUrl(ADMIN_WATCH_TOWER_INCIDENT, publicId),
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<IWatchIncidentDetail>(response),
      providesTags: (_result, _error, publicId) => [
        { type: WATCH_TOWER_INCIDENT_TAG, id: publicId },
      ],
    }),

    openWatchIssueChat: builder.mutation<IWatchChatBind, string>({
      query: (publicId) => ({
        url: publicIdUrl(ADMIN_WATCH_TOWER_INCIDENT_CHATS, publicId),
        method: HTTP_METHODS.POST,
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<IWatchChatBind>(response),
      invalidatesTags: [WATCH_TOWER_CHATS_TAG],
    }),

    getWatchIssueChats: builder.query<IWatchIssueChatRecord[], string>({
      query: (publicId) => ({
        url: publicIdUrl(ADMIN_WATCH_TOWER_INCIDENT_CHATS, publicId),
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<IWatchIssueChatRecord[]>(response),
      providesTags: [WATCH_TOWER_CHATS_TAG],
    }),

    addWatchKnownList: builder.mutation<unknown, IWatchKnownListRequest>({
      query: ({ publicId, ...body }) => ({
        url: publicIdUrl(ADMIN_WATCH_TOWER_INCIDENT_KNOWN_LIST, publicId),
        method: HTTP_METHODS.POST,
        body,
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<unknown>(response),
      invalidatesTags: (_result, _error, { publicId }) => [
        WATCH_TOWER_INCIDENTS_TAG,
        { type: WATCH_TOWER_INCIDENT_TAG, id: publicId },
      ],
    }),

    runWatchMatcher: builder.mutation<unknown, string>({
      query: (publicId) => ({
        url: publicIdUrl(ADMIN_WATCH_TOWER_INCIDENT_MATCHER, publicId),
        method: HTTP_METHODS.POST,
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<unknown>(response),
      invalidatesTags: (_result, _error, publicId) => [
        WATCH_TOWER_INCIDENTS_TAG,
        { type: WATCH_TOWER_INCIDENT_TAG, id: publicId },
      ],
    }),

    sendWatchDigest: builder.mutation<unknown, void>({
      query: () => ({
        url: ADMIN_WATCH_TOWER_DIGEST_SEND,
        method: HTTP_METHODS.POST,
      }),
      transformResponse: (response: unknown) => unwrapWatchTowerBody<unknown>(response),
    }),
  }),
});

export const {
  useGetWatchCompaniesQuery,
  useGetWatchCompanyQuery,
  useGetWatchCompanyDossierQuery,
  useGetWatchCompanyRunsQuery,
  useGetWatchRunLlmStepsQuery,
  useImportWatchCompaniesMutation,
  useCreateWatchCompaniesMutation,
  useApproveWatchInvestigationMutation,
  useCancelWatchInvestigationMutation,
  useRetryWatchInvestigationMutation,
  useContinueWatchInvestigationMutation,
  useOpenWatchCreateChatMutation,
  useOpenWatchDossierChatMutation,
  useGetWatchTopicsQuery,
  useGetWatchTopicQuery,
  useCreateWatchTopicMutation,
  usePatchWatchTopicMutation,
  useGetWatchSchedulesQuery,
  useGetWatchScheduleQuery,
  useCreateWatchScheduleMutation,
  useGetWatchIncidentsQuery,
  useGetWatchIncidentQuery,
  useOpenWatchIssueChatMutation,
  useGetWatchIssueChatsQuery,
  useAddWatchKnownListMutation,
  useRunWatchMatcherMutation,
  useSendWatchDigestMutation,
} = watchTowerApi;

export default watchTowerApi;
